import { TextInput, Text } from '@mantine/core';
import React, { useContext, useEffect, useState } from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { HomeContext, UserContext } from '../../lib/context';
import { useFirebaseRefs } from "../../lib/firebase";
import {
    PlaceRecord,
    SearchEngineResult,
    Guide,
    UpdateTripGuideSectionType,
} from '../../lib/interfaces';
import { SearchChipsAndList } from "../SearchUI/SearchUI";
import { Position, positionFromLatLng } from "../../lib/position";
import { raceOrNotify } from "../../lib/promises";
import { SheetsData } from "../Sheets";
import { newSearchQuery } from "../../lib/search";
import { blurOnEnterKey } from "../../lib/eventHandlers";
import { StepWizard } from "../StepWizard/StepWizard";
import { computeBias } from "../../lib/geolocation";
import stepWizardClasses from "../StepWizard/StepWizard.module.css";
import { ClearTextAreaRightSection } from '../AddEditTrip/ClearTextAreaRightSection';
import {
    deleteGuideSlugDocsFromGuideId,
    generateSlugFromTitle,
    insertGuide,
    insertGuideSlug,
    updateGuide,
} from '../../lib/guideFunctions';
import { useGuideSlugByGuideId } from '../../lib/hooks';

export function titleUniqueCheck(title: string, uidGuides: Guide[] | undefined, currentGuideDocid?: string | null): boolean | 'loading' {
    const slug = generateSlugFromTitle(title);
    if (uidGuides === undefined) return 'loading';

    return !uidGuides.some(guide => {
        if (currentGuideDocid && guide.docid === currentGuideDocid) {
            return false;
        }
        const guideSlug = generateSlugFromTitle(guide.title);
        return guideSlug === slug;
    });
}

/**
 * Shows a series of modal sheets collecting information to create or edit a guide
 */
export async function showAddEditGuide(sheets: SheetsData, guideTemplate: Partial<Guide>, updateSection?: UpdateTripGuideSectionType) {
    const title = (() => {
        if (guideTemplate.docid) {
            return `Update Guide`;
        } else {
            return 'Add New Guide';
        }
    })();

    sheets.showSheetWith(
        context => <AddEditGuide
            onClose={() => context.removeSheet()}
            guide={guideTemplate}
            updateSection={updateSection} />,
        {
            title,
            disableDrag: true,
        }
    );
}

type AddEditGuideForm = UseFormReturnType<{
    title: string;
    location: string;
},
    (values: {
        title: string;
        location: string;
    }) => {
        title: string;
        location: string;
    }>;

function SectionZeroEditGuide(props: {
    form: AddEditGuideForm,
    onClear: () => void,
    values: Partial<Guide>,
    replacer: () => void,
    currentUserId: string | undefined,
    onClick: (result: PlaceRecord | SearchEngineResult) => Promise<void> }) {
    return <div
        className={stepWizardClasses.stepSection}
        style={{ flex: '1', display: 'flex', flexDirection: 'column' }}
    >
        <hr style={{ width: "100%" }} />
        <div
            className={stepWizardClasses.progress}
            style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
            }}>
            <TextInput
                label='1. Location'
                autoFocus
                required
                style={{
                    display: "inline-block",
                    flex: 1,
                }}
                {...props.form.getInputProps("location")}
                onKeyUp={blurOnEnterKey}
                rightSection={<ClearTextAreaRightSection onClear={props.onClear}/>}
            />
        </div>
        <SearchChipsAndList
            searchQuery={newSearchQuery({
                    searchTerm: (props.form.values as any).location,
                    chipConfig: {
                        tripGuideChips: false,
                        savedPlaces: false,
                        recents: false,
                        searchEngine: true,
                    },
                    emptyQueryBehavior: "match-none",
                    bias: computeBias([ positionFromLatLng(props.values) ]),
                    searchType: 'region',
                    sources: {
                        yourSaved: false,
                        otherSaved: false,
                        recentTrips: false,
                        searchEngine: true,
                    },
                },
                props.replacer)
            }
            places={[]}
            userTrips={[]}
            currentUserId={props.currentUserId ?? undefined}
            uiState="inline"
            detailMode="results"
            notifySearchHits={props.replacer}
            onClick={props.onClick}/>
    </div>;
}

function SectionOneEditGuide(props: {
    form: AddEditGuideForm,
    onInput: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onClear: () => void,
    username: string | null,
    slug: string | null }) {
    return <div className={stepWizardClasses.stepSection}>
        <hr style={{ width: "100%" }} />
        <div className={stepWizardClasses.progress}>
            1. Location: <span className={stepWizardClasses.fieldValue}>{props.form.values.location}</span>
        </div>
        <div
            className={stepWizardClasses.progress}
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <TextInput
                required
                autoFocus
                label="2. Title:"
                style={{ display: "inline-block", flex: 1 }}
                {...props.form.getInputProps("title")}
                onInput={props.onInput}
                enterKeyHint="done"
                onKeyUp={blurOnEnterKey}
                rightSection={<ClearTextAreaRightSection onClear={props.onClear} />}
            />
        </div>
        {props.slug && props.username && (
            <div
                className={stepWizardClasses.progress}
                style={{ color: 'dimgray', fontStyle: 'italic' }}>
                <Text size='sm'>Note: The guide url does not change</Text>
                <Text size='sm'>app.travelscroll.com/guides/{props.username}/{props.slug}</Text>
            </div>
        )}
    </div>;
}

export function AddEditGuide(
    {
        guide,
        onClose,
        updateSection,
    }: {
        guide: Partial<Guide>,
        onClose: () => unknown,
        updateSection?: UpdateTripGuideSectionType,
    }) {
    const firebaseRefs = useFirebaseRefs();
    const { uidGuides } = useContext(HomeContext);
    const { firebaseUserId: currentUserId } = useContext(UserContext);
    const user = useContext(UserContext);

    const [ currentStep, setCurrentStep ] = useState(0);

    // Determine if this is a new guide (no docid yet)
    const isNewGuide = !guide.docid;

    // Fetch the actual slug from Firestore — this is the real slug that was
    // created when the guide was first made, regardless of title changes.
    const existingSlug = useGuideSlugByGuideId(currentUserId, guide.docid);

    // Leave all these values undefined unless the user makes a change. Calls to setDoc() on guide edit
    // should be minimal, so that concurrent editors conflict minimally.
    const [ position, setPosition ] = useState<Position | undefined>();
    const [ googlePlaceId, setGooglePlaceId ] = useState<string | undefined>();
    const [ googleLocality, setGoogleLocality ] = useState<string>();

    const form = useForm({
        initialValues: {
            title: guide.title ?? '',
            location: guide.location ?? '',
        },
        validate: (values) => {
            if (
                (updateSection === undefined && currentStep === 0) ||
                updateSection === 'Location'
            ) {
                return {
                    location: !values.location || values.location.trim().length < 1
                        ? 'A location is required'
                        : null,
                };
            } else if (
                (updateSection === undefined && currentStep === 1) ||
                updateSection === 'Title'
            ) {
                const { title } = values;
                const titleUnique = titleUniqueCheck(title, uidGuides, guide.docid ?? null);
                return {
                    title:
                        !values.title || values.title.trim().length < 1
                            ? 'A title is required'
                            : titleUnique === 'loading'
                                ? 'Checking title availability...'
                                : !titleUnique
                                    ? 'A unique title is required'
                                    : null,
                };
            } else {
                return {};
            }
        },
    });

    useEffect(() => {
        if (updateSection === 'Title') {
            setCurrentStep(1);
        }
    }, [ updateSection ]);

    const upsertGuide = async (): Promise<string> => {
        const partialGuide = buildGuideRecordFromInternalState();

        if (guide.docid) {
            await updateGuide(firebaseRefs, guide.docid, user, partialGuide);
            return guide.docid; // existing id
        } else {
            const newId = await insertGuide(firebaseRefs, user, partialGuide);
            return newId; // make sure insertGuide returns the new id
        }
    };

    function buildGuideRecordFromInternalState() {
        const partialGuide: Partial<Guide> = {
            location: form.values.location,
            title: form.values.title,
            locality: googleLocality,
        };

        if (position) {
            partialGuide.lat = position.lat;
            partialGuide.lng = position.lng;
        }

        if (googlePlaceId) {
            partialGuide.placeid = googlePlaceId;
        }

        if (googleLocality) {
            partialGuide.locality = googleLocality;
        }

        return partialGuide;
    }

    const steps = updateSection === undefined ? [ "Location", "Title" ] : (updateSection === "Location") ? [ "Location" ] : [ "Title" ];
    const nextStep = () =>
        setCurrentStep((current) => {
            if (form.validate().hasErrors) {
                return current;
            }
            if (updateSection === undefined) {
                return current < steps.length ? Math.min(current + 1, steps.length - 1) : current;
            } else {
                return current;
            }
        });

    return <StepWizard
        updateSection={updateSection}
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        nextStep={nextStep}
        onDone={async () => {
            if (form.validate().hasErrors) {
                return setCurrentStep((current) => current);
            }

            const title = form.values.title ?? form.values.location;
            const detail = title || '';

            try {
                const guideDocId = await upsertGuide();

                // Only create the slug when the guide is first created;
                // existing guides keep their original slug forever.
                if (isNewGuide) {
                    try {
                        await deleteGuideSlugDocsFromGuideId(firebaseRefs, guideDocId, user);
                        await insertGuideSlug(firebaseRefs, guideDocId, title, user.firebaseUserId);
                    } catch (slugError) {
                        console.error('Failed to create guide slug:', slugError);
                        notifications.show({
                            title: 'Warning',
                            message: `Guide "${detail}" was saved, but the URL slug could not be created.`,
                            color: 'yellow',
                            autoClose: 5000,
                        });
                    }
                }

                // No username is claimed here. A username only exists to address a *published*
                // guide (app.travelscroll.com/guides/{username}/{slug}), it is globally unique, and
                // nothing in the app can change it once claimed — so it is minted on first publish
                // (see showPublishGuideModal) rather than when a private guide is saved. Claiming it
                // here permanently reserved a handle, derived from the user's email address when
                // they had no display name, for someone who might never publish anything.

                // Deliberately no form.reset() here. The sheet is about to unmount, so resetting
                // achieves nothing except blanking the still-visible Title field back to its
                // initial value (empty for a new guide) for the frame before the sheet closes —
                // which then renders the empty required field as though the save had failed.
                return onClose();
            } catch (error) {
                console.error('Failed to save guide:', error);
                notifications.show({
                    title: 'Error',
                    message: `Failed to save guide "${detail}". Please try again.`,
                    color: 'red',
                    autoClose: 5000,
                });
            }
        }}
    >
        {currentStep === 0 && <>
            <SectionZeroEditGuide
                form={form}
                onClear={() => form.setFieldValue('location', '')}
                values={guide}
                replacer={() => {}}
                currentUserId={currentUserId}
                onClick={async (result) : Promise<void> => {
                    if (result.type === 'place') {
                        if (result.place.location) {
                            form.setFieldValue('location', result.place.location);
                            if (!form.values.title || form.values.title === '') {
                                // only set the 'title' field value if we haven't done so already, either
                                // via out-of-order components or via pre-existing values (e.g., an edit)
                                form.setFieldValue('title', result.place.location);
                            }
                        }
                        setPosition(positionFromLatLng(result.place));
                        setGooglePlaceId(result.place.placeid ?? undefined);
                    } else {
                        if (result.title) {
                            form.setFieldValue('location', result.title);
                            if (!form.values.title || form.values.title === '') {
                                // only set the 'title' field value if we haven't done so already, either
                                // via out-of-order components or via pre-existing values (e.g., an edit)
                                form.setFieldValue('title', result.title);
                            }
                        }
                        const [ resultPosition, locality ] = await Promise.all([
                            await result.position(),
                            await result.locality(),
                        ]);
                        setPosition(resultPosition);
                        setGoogleLocality(locality);
                        if (result.type === 'google-search') {
                            setGooglePlaceId(result.value.place_id);
                        }
                    }

                    if (updateSection === undefined) {
                        nextStep();
                    } else {
                        if (form.validate().hasErrors) {
                            setCurrentStep((current) => current);
                        } else {
                            const title = form.values.title ?? form.values.location;
                            const detail = title || '';
                            await raceOrNotify(
                                upsertGuide(),
                                500,
                                `Saved ${detail}`,
                                `Failed to save ${detail}!`
                            );
                            // No form.reset() — see the note in the other done handler: it only
                            // blanks the visible field for a frame before the sheet unmounts.
                            onClose();
                        }
                    }
                }} />
        </>}

        { currentStep === 1 && <SectionOneEditGuide
            form={form}
            onInput={e => form.setFieldValue('title', e.currentTarget.value)}
            onClear={() => form.setFieldValue('title', '')}
            username={user.username ?? null}
            slug={existingSlug} /> }
    </StepWizard>;
}
